import { Table, TableBody, TableCell, TableHead, TableRow } from "../../../../../common/components/Table/TableElements";
import dayjs from "dayjs";
import { NumberFormatter } from "../../../../../utils/NumberFormatter";
import Spacer from "../../../../../common/components/Spacer";

import classes from "./PartialDeliveriesSection.module.less";
import classNames from "classnames";
import { DateFormatType } from "../../../../../utils/DateFormatter";
import { Trans, useTranslation } from "react-i18next";
import { useLayoutEffect, useRef, useState } from "react";
import { Collapse } from "~/common/components/Collapse";
import { LinkButton } from "../../../../../common/components/Link";
import { IconButton } from "@mui/material";
import { ToggleIcon } from "../../../../../common/components/icon/ToggleIcon";
import { getPhysicalDeliveries, isReplenishment } from "~/utils/cartUpdating/delivery/stockDataUtils";
import { type StockData } from "~/utils/cartUpdating/delivery/stockDataTypes";
import { Alert } from "~/common/m3/Alert/Alert";
import { formatShippingOrDeliveryDate } from "../../../../../utils/dateUtils";

interface PartialDeliveriesSectionProps {
  partialDeliveries?: StockData;
  packagingIsExpanded?: boolean;
  isCheckout?: boolean;
}

export const PartialDeliveriesSection = ({
  packagingIsExpanded,
  partialDeliveries,
  isCheckout,
}: PartialDeliveriesSectionProps) => {
  const { t } = useTranslation();
  const [expanded, setIsExpanded] = useState(false);
  const [sectionExpanded, setSectionExpanded] = useState(true);
  const tableRef = useRef<HTMLTableElement>(null);
  const [closedHeight, setClosedHeight] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    if (partialDeliveries && !isCheckout) {
      if (tableRef.current) {
        const rows = tableRef.current.querySelectorAll("tr");
        // 6 = header + 5 rows
        const totalHeight = Array.from(rows)
          .slice(0, 6)
          .map((row) => row.clientHeight)
          .reduce((a, b) => a + b, 0);
        setClosedHeight(totalHeight);
      }
    }
  }, [isCheckout, partialDeliveries]);

  if (!isPartialDeliveriesVisible(partialDeliveries)) {
    return null;
  }

  const replenishmentDelivery = partialDeliveries.find(isReplenishment);
  const tableDeliveries = getPhysicalDeliveries(partialDeliveries);

  const sectionCls = classNames(
    classes.section,
    packagingIsExpanded && classes.sectionExtraSpaceTop,
    isCheckout && classes.sectionCheckoutVariant,
  );

  if (tableDeliveries.length <= 1) {
    if (replenishmentDelivery) {
      return (
        <div className={sectionCls}>
          <Alert severity={"info"} className={classes.loneReplenishmentMsg}>
            {t("partialDeliveries.replenishment.full", getNumberProps(replenishmentDelivery.amount))}
          </Alert>
        </div>
      );
    }
    return null;
  }

  const today = dayjs().startOf("day");
  const getDays = (date: Date) => dayjs(date).startOf("day").diff(today, "day");

  const showMoreLess = tableDeliveries.length > 5 && !isCheckout;

  let table = (
    <Table ref={tableRef}>
      <TableHead>
        <TableRow>
          <TableCell>{t("wishlistPage.table.header.number")}</TableCell>
          <TableCell>{t("input.quantity")}</TableCell>
          <TableCell>{t("orderList.date")}</TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {tableDeliveries.map((entry, index) => {
          const count = getDays(entry.date!);
          return (
            <TableRow>
              <TableCell>{index + 1}.</TableCell>
              <TableCell>{t("common.piece", { count: entry.amount })}</TableCell>
              <TableCell>{formatShippingOrDeliveryDate(entry.date!, DateFormatType.DATE, "")}</TableCell>
              <TableCell>
                <Trans i18nKey={`partialDeliveries.shipment.${count === 0 ? "today" : "default"}`} count={count}>
                  shipping expected in <b>{{ count }} day</b>
                </Trans>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );

  if (showMoreLess) {
    table = (
      <Collapse in={expanded} collapsedSize={closedHeight}>
        {table}
      </Collapse>
    );
  }

  return (
    <>
      <div className={sectionCls}>
        <Spacer className={classes.divider} />
        <p className={classes.title}>
          {t("partialDeliveries.title", { count: tableDeliveries.length })}
          <IconButton color={"primary"} classes={{ root: classes.collapseSectionBtn }}>
            <ToggleIcon isActive={sectionExpanded} onClick={() => setSectionExpanded(!sectionExpanded)} />
          </IconButton>
        </p>
        <Collapse in={sectionExpanded} className={classes.sectionDetails}>
          {table}
          {showMoreLess && (
            <div className={classes.showMoreContainer}>
              <LinkButton onClick={() => setIsExpanded(!expanded)}>
                {t(expanded ? "searchPage.refinements.showLess" : "availabilityTable.showAll")}
              </LinkButton>
            </div>
          )}
        </Collapse>
        {replenishmentDelivery && (
          <Alert severity={"info"} className={"mt-2"}>
            {t("partialDeliveries.replenishment.partial", getNumberProps(replenishmentDelivery.amount))}
          </Alert>
        )}
      </div>
    </>
  );
};

const getNumberProps = (count: number) => ({ count, amount: NumberFormatter.getDefaultFormatter().format(count) });

export function isPartialDeliveriesVisible(partialDeliveries?: StockData): partialDeliveries is StockData {
  return !!(partialDeliveries && partialDeliveries.length);
}
